'use client'

import { useLocale, useTranslations } from 'next-intl'
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'

import type { NotificationItem } from '../../api/notificationsApi'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationsFeed,
  useUnreadCount
} from '../../hooks/useNotificationsFeed'
import { useNotificationsStream } from '../../hooks/useNotificationsStream'
import { notificationHref } from '../../lib/notificationHref'
import { popoverOffsets } from '../../lib/popoverOffsets'
import { renderNotification } from '../../lib/renderNotification'
import { NotificationsPanel } from '../NotificationsPanel'
import { BellIcon } from './BellIcon'
import S from './NotificationBell.module.scss'

/**
 * Set on `window` by the inline script of the layout when the placeholder
 * bell is pressed before the app has hydrated (FE-28).
 */
export const BELL_PENDING_FLAG = '__arenaBellPending'

function consumePendingClick(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  const scope = window as unknown as Record<string, unknown>
  if (!scope[BELL_PENDING_FLAG]) {
    return false
  }
  delete scope[BELL_PENDING_FLAG]
  return true
}

const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const isTabbable = (node: HTMLElement) =>
  !node.closest('[hidden], [inert], [aria-hidden="true"]') &&
  (node.checkVisibility?.() ?? true)

const tabbablesIn = (root: ParentNode) =>
  Array.from(root.querySelectorAll<HTMLElement>(TABBABLE)).filter(node =>
    isTabbable(node)
  )

export function NotificationBell() {
  const t = useTranslations('notifications')
  const locale = useLocale()
  const panelId = useId()
  // The bell mounts on the client only (the header shows a placeholder until
  // the session is known), so a click made on that placeholder before
  // hydration opens it straight away.
  const [isOpen, setIsOpen] = useState(consumePendingClick)
  const [announcement, setAnnouncement] = useState('')
  const buttonRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  const { query, items } = useNotificationsFeed(isOpen)
  const unread = useUnreadCount()
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()

  // Screen readers hear about a notification that arrives while the fan is
  // on the page; the badge alone changes silently (FE-8).
  const announce = useCallback(
    (notification: NotificationItem) => {
      const { title } = renderNotification(
        notification,
        (key, values) => t(key, values),
        key => t.has(key),
        locale
      )
      setAnnouncement(t('announceNew', { title }))
    },
    [t, locale]
  )

  // The stream runs whether or not the panel is open: the badge has to light
  // up while the fan is looking at something else.
  useNotificationsStream(true, announce)

  const close = useCallback((restoreFocus: boolean) => {
    setIsOpen(false)
    if (restoreFocus) {
      buttonRef.current?.focus()
    }
  }, [])

  // Measured before paint, so the panel never flashes at a stale spot.
  useLayoutEffect(() => {
    if (!isOpen) {
      return
    }

    const place = () => {
      const button = buttonRef.current
      const popover = popoverRef.current
      if (!button || !popover) {
        return
      }

      const { top, right, width, maxHeight } = popoverOffsets(
        button.getBoundingClientRect(),
        {
          width: document.documentElement.clientWidth,
          height: window.innerHeight
        }
      )

      popover.style.setProperty('--notifications-popover-top', `${top}px`)
      popover.style.setProperty('--notifications-popover-right', `${right}px`)
      popover.style.setProperty('--notifications-popover-width', `${width}px`)
      popover.style.setProperty(
        '--notifications-popover-max-height',
        `${maxHeight}px`
      )
    }

    place()
    window.addEventListener('resize', place)

    return () => window.removeEventListener('resize', place)
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      panelRef.current?.focus({ preventScroll: true })
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const inside = (target: EventTarget | null) =>
      target instanceof Node &&
      (Boolean(buttonRef.current?.contains(target)) ||
        Boolean(popoverRef.current?.contains(target)))

    const handlePointerDown = (event: PointerEvent) => {
      if (!inside(event.target)) {
        close(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      // A dialog opened over the panel handles its own Escape first.
      if (event.key === 'Escape' && !event.defaultPrevented) {
        event.preventDefault()
        close(true)
      }
    }
    const handleFocusIn = (event: FocusEvent) => {
      if (!inside(event.target)) {
        close(false)
      }
    }
    // The panel is fixed to the viewport while the header scrolls with the
    // page; rather than drift away from the bell it closes (FE-4). Scrolling
    // the list itself stays inside the panel (overscroll-behavior).
    const handleScroll = () => close(false)

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('focusin', handleFocusIn)
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('focusin', handleFocusIn)
      window.removeEventListener('scroll', handleScroll)
    }
  }, [isOpen, close])

  // The panel is rendered at the end of <body>, so the tab order is stitched
  // by hand: bell → panel → whatever follows the bell in the header.
  const handleButtonKeyDown = (event: ReactKeyboardEvent) => {
    if (!isOpen || event.key !== 'Tab' || event.shiftKey) {
      return
    }
    const popover = popoverRef.current
    const first = popover ? tabbablesIn(popover)[0] : undefined
    event.preventDefault()
    ;(first ?? panelRef.current)?.focus()
  }

  const handlePanelKeyDown = (event: ReactKeyboardEvent) => {
    const popover = popoverRef.current
    const button = buttonRef.current
    if (event.key !== 'Tab' || !popover || !button) {
      return
    }

    const inPanel = tabbablesIn(popover)
    const active = document.activeElement

    if (event.shiftKey) {
      if (active === panelRef.current || active === inPanel[0]) {
        event.preventDefault()
        button.focus()
      }
      return
    }

    if (inPanel.length === 0 || active === inPanel.at(-1)) {
      event.preventDefault()
      const page = tabbablesIn(document).filter(node => !popover.contains(node))
      const next = page[page.indexOf(button) + 1]
      close(false)
      ;(next ?? button).focus()
    }
  }

  const handleOpenItem = useCallback(
    (item: NotificationItem, event: MouseEvent) => {
      markRead.mutate(item.id)

      const newTab =
        event.button === 1 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      // Followed here: the panel closes and focus waits on the bell, which
      // stays in the header across the navigation.
      if (notificationHref(item.action_url) && !newTab) {
        close(true)
      }
    },
    [markRead, close]
  )

  const count = unread.data ?? 0
  const badge = count > 99 ? '99+' : String(count)

  return (
    <div className={S.wrap}>
      <button
        ref={buttonRef}
        type="button"
        className={S.button}
        aria-label={count > 0 ? t('unreadCount', { count }) : t('open')}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        onClick={() => setIsOpen(open => !open)}
        onKeyDown={handleButtonKeyDown}
      >
        <BellIcon />
        {count > 0 ? (
          <span className={S.badge} aria-hidden="true">
            {badge}
          </span>
        ) : null}
      </button>

      <div className={S.srOnly} aria-live="polite" aria-atomic="true">
        {announcement}
      </div>

      {isOpen
        ? createPortal(
            <div
              ref={popoverRef}
              className={S.popover}
              onKeyDown={handlePanelKeyDown}
            >
              <NotificationsPanel
                ref={panelRef}
                id={panelId}
                items={items}
                isLoading={query.isLoading}
                isError={query.isError}
                onRetry={() => void query.refetch()}
                hasMore={query.hasNextPage}
                isLoadingMore={query.isFetchingNextPage}
                onLoadMore={() => void query.fetchNextPage()}
                onOpen={handleOpenItem}
                onDismiss={item => markRead.mutate(item.id)}
                onClearAll={() => markAllRead.mutate()}
              />
            </div>,
            document.body
          )
        : null}
    </div>
  )
}
